import React, { useEffect, useState, useMemo } from 'react';
import { Link, useNavigate, useSearchParams, useLoaderData } from 'react-router-dom';
import { PlusIcon, MusicalNoteIcon, ChevronDownIcon, ChevronUpIcon, CheckIcon, UsersIcon, SparklesIcon } from '@heroicons/react/24/outline';
import { Search } from 'lucide-react';
//
import { deleteTrack, uncollaborate } from '../services/trackService';
import type { Track, AudioFile } from '../services/trackTypes';
import { useTranslation } from 'react-i18next';
import { getTierLimits } from '../constants/tierLimits';
import { useAuth } from '../contexts/AuthContext';
import { logInfo, logError } from '../services/logtail';
import { resolveTrackMediaUrl } from '../services/mediaAccess';
import { getAuth } from 'firebase/auth';
import { sanitizeSearchQuery } from '../utils/sanitization';
import AudioWavePlayer from '../components/AudioWavePlayer';
import PlaybackControls from '../components/PlaybackControls';
import KanbanBoard from '../components/KanbanBoard';
import { changeTrackStatus, type KanbanSort } from '../services/kanbanService';
import toast from 'react-hot-toast';
import { Listbox } from '@headlessui/react';
//
import TrackTile from '../components/TrackTile';
import StatusChangeNotification from '../components/StatusChangeNotification';
import { type ShareWithTrack } from '../services/shareService';
import type { DashboardLoaderData } from '../auth/routeGuards';
import { cleanUtmFromUrlIfStored } from '../utils/utm';
import { SketchPageTitle, SketchButton } from '../components/sketchy';
import CassetteLoader from '../components/CassetteLoader';
import RequireAccountModal from '../components/RequireAccountModal';
import { useStorageGate } from '../hooks/useStorageGate';

// Tooltip moved to TrackTile; not needed here

const Dashboard: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [tracks, setTracks] = useState<Track[]>([]);
  const [sharedTracks, setSharedTracks] = useState<ShareWithTrack[]>([]);
  const [tracksLoading, setTracksLoading] = useState(true);
  
  // Initialize viewScope from URL, localStorage, or default to 'owned'
  const getInitialViewScope = (): 'owned' | 'shared' | 'all' => {
    const urlTab = searchParams.get('tab') as 'owned' | 'shared' | 'all';
    if (urlTab && ['owned', 'shared', 'all'].includes(urlTab)) {
      return urlTab;
    }
    
    // Fallback to localStorage if no URL tab
    const savedTab = localStorage.getItem('dashboard-viewScope') as 'owned' | 'shared' | 'all';
    if (savedTab && ['owned', 'shared', 'all'].includes(savedTab)) {
      return savedTab;
    }
    
    return 'owned';
  };
  
  const [viewScope, setViewScope] = useState<'owned' | 'shared' | 'all'>(getInitialViewScope());
  const { t } = useTranslation();
  const [playingTrackId, setPlayingTrackId] = useState<string | null>(null);
  const [isPaused, setIsPaused] = useState(false);
  const [sortBy, setSortBy] = useState<'created' | 'updated' | 'name'>(() => {
    const saved = localStorage.getItem('dashboard-sortBy');
    return (saved as 'created' | 'updated' | 'name') || 'created';
  });
  const [viewMode, setViewMode] = useState<'tiles' | 'kanban'>(() => (localStorage.getItem('dashboard:viewMode') as 'tiles' | 'kanban') || 'tiles');
  const [showReleased, setShowReleased] = useState(true);
  const [search, setSearch] = useState('');
  const { userType, user, isAnonymous, linkWithGoogle, linkWithApple } = useAuth();
  const { canCreate } = useStorageGate();
  const navigate = useNavigate();
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [showRequireAccount, setShowRequireAccount] = useState(false);
  const [artworkBlobs, setArtworkBlobs] = useState<{ [trackId: string]: string | null }>({});
  const [artworkLoading, setArtworkLoading] = useState<{ [trackId: string]: boolean }>({});
  const [artworkModal, setArtworkModal] = useState<{ trackId: string, url: string } | null>(null);
  const [showFilters, setShowFilters] = useState(() => {
    const saved = localStorage.getItem('dashboard:showFilters');
    return saved !== null ? saved === 'true' : true;
  });
  const [deletingTracks, setDeletingTracks] = useState<Set<string>>(new Set());
  const [uncollaboratingTracks, setUncollaboratingTracks] = useState<Set<string>>(new Set());
  const [trackShareCounts, setTrackShareCounts] = useState<Record<string, number>>({});

  // Sync viewScope with URL changes
  useEffect(() => {
    const tabFromUrl = searchParams.get('tab') as 'owned' | 'shared' | 'all';
    if (tabFromUrl && tabFromUrl !== viewScope) {
      setViewScope(tabFromUrl);
      localStorage.setItem('dashboard-viewScope', tabFromUrl);
    }
  }, [searchParams, viewScope]);

  // Restore tab state when component mounts without URL tab (e.g., back navigation)
  useEffect(() => {
    const urlTab = searchParams.get('tab');
    if (!urlTab) {
      const savedTab = localStorage.getItem('dashboard-viewScope') as 'owned' | 'shared' | 'all';
      if (savedTab && savedTab !== viewScope) {
        setViewScope(savedTab);
        setSearchParams({ tab: savedTab });
      }
    }
  }, []); // Only run on mount

  // Update URL and localStorage when viewScope changes
  const handleViewScopeChange = (newScope: 'owned' | 'shared' | 'all') => {
    setViewScope(newScope);
    setSearchParams({ tab: newScope });
    localStorage.setItem('dashboard-viewScope', newScope);
  };

  // Track data is fetched by dashboardLoader and arrives as still-pending promises,
  // so the Layout shell paints instantly and this content area fills in.
  const loaderData = useLoaderData() as DashboardLoaderData;
  useEffect(() => {
    let active = true;

    // Clean UTM params from URL now that user is past onboarding and params are in localStorage
    cleanUtmFromUrlIfStored();

    logInfo('Loading dashboard tracks');
    loaderData.tracks
      .then((data) => {
        if (!active) return;
        setTracks(data);
        setTracksLoading(false);
        logInfo('Dashboard tracks loaded successfully', { trackCount: data.length });
      })
      .catch(() => {
        if (active) setTracksLoading(false);
      });

    loaderData.shared
      .then((data) => {
        if (!active) return;
        setSharedTracks(data);
        logInfo('Shared tracks loaded', { count: data.length });
      })
      .catch((error) => {
        logError('Failed to load shared tracks', { error });
      });

    return () => {
      active = false;
    };
  }, [loaderData]);

  // Auto-switch to 'shared' tab if user has no owned tracks but has shared tracks
  useEffect(() => {
    if (!tracksLoading && tracks.length === 0 && sharedTracks.length > 0 && viewScope === 'owned') {
      setViewScope('shared');
      setSearchParams({ tab: 'shared' });
      localStorage.setItem('dashboard-viewScope', 'shared');
    }
  }, [tracksLoading, tracks.length, sharedTracks.length, viewScope, setSearchParams]);

  // Load share counts when tracks change - now using embedded shareCount from backend
  useEffect(() => {
    if (tracks.length > 0) {
      const counts: Record<string, number> = {};
      for (const track of tracks) {
        // Use embedded shareCount from backend optimization, fallback to 0
        counts[track.id] = track.shareCount || 0;
      }
      setTrackShareCounts(counts);
      logInfo('Share counts loaded from embedded data', { 
        trackCount: tracks.length, 
        totalShares: Object.values(counts).reduce((sum, count) => sum + count, 0)
      });
    }
  }, [tracks]);

  // Sorting and filtering logic - memoized to prevent unnecessary recalculations
  const filteredTracks = useMemo(() => {
    let result: Track[] = [];
    
    // Determine which tracks to show based on viewScope
    if (viewScope === 'owned') {
      result = tracks;
    } else if (viewScope === 'shared') {
      result = sharedTracks.map(st => st.track).filter((t): t is Track => t !== undefined);
    } else { // 'all'
      const sharedTrackList = sharedTracks.map(st => st.track).filter((t): t is Track => t !== undefined);
      result = [...tracks, ...sharedTrackList];
    }
    
    if (!showReleased) {
      result = result.filter(t => t.status !== 'Live');
    }
    if (search.trim()) {
      const sanitizedSearch = sanitizeSearchQuery(search);
      result = result.filter(t => t.name.toLowerCase().includes(sanitizedSearch.toLowerCase()));
    }
    return [...result].sort((a, b) => {
      if (sortBy === 'name') {
        return a.name.localeCompare(b.name);
      } else if (sortBy === 'created') {
        return new Date(b.createdDate).getTime() - new Date(a.createdDate).getTime();
      } else if (sortBy === 'updated') {
        const aUpdated = (a as Track & { lastUpdated?: string }).lastUpdated || a.createdDate;
        const bUpdated = (b as Track & { lastUpdated?: string }).lastUpdated || b.createdDate;
        return new Date(bUpdated).getTime() - new Date(aUpdated).getTime();
      }
      return 0;
    });
  }, [tracks, sharedTracks, viewScope, showReleased, search, sortBy]);

  const [movingIds, setMovingIds] = useState<Set<string>>(new Set());
  const [statusChangeNotification, setStatusChangeNotification] = useState<{
    trackName: string;
    newStatus: string;
    success: boolean;
  } | null>(null);
  const handleMoveKanban = async (trackId: string, toStatus: string) => {
    const track = tracks.find(t => t.id === trackId);
    if (!track) return;

    // Optimistic UI update - immediately update the track status and lastUpdated
    const now = new Date().toISOString();
    setTracks(prev => prev.map(t => (t.id === trackId ? { ...t, status: toStatus, lastUpdated: now } : t)));
    
    try {
      setMovingIds(prev => new Set(prev).add(trackId));
      const updated = await changeTrackStatus(trackId, toStatus);
      
      // Update with the actual response from server
      setTracks(prev => prev.map(t => (t.id === trackId ? { ...t, status: updated.status } : t)));
      
      // Show success notification
      setStatusChangeNotification({
        trackName: track.name,
        newStatus: updated.status,
        success: true
      });
    } catch (e) {
      // Rollback the optimistic update on error
      setTracks(prev => prev.map(t => (t.id === trackId ? { ...t, status: track.status, lastUpdated: track.lastUpdated } : t)));
      
      // Show error notification
      setStatusChangeNotification({
        trackName: track.name,
        newStatus: toStatus,
        success: false
      });
      
      logError('Failed to update track status via kanban', { trackId, toStatus, error: e });
    } finally {
      setMovingIds(prev => {
        const next = new Set(prev);
        next.delete(trackId);
        return next;
      });
    }
  };

  useEffect(() => {
    // Fetch artwork blobs for tracks with artwork
    // Include both owned tracks and shared tracks
    const allTracks = [
      ...tracks,
      ...sharedTracks.map(st => st.track).filter((t): t is Track => t !== undefined)
    ];
    
    allTracks.forEach(track => {
      if (track.artwork && track.artwork.fileId && !artworkBlobs[track.id] && !artworkLoading[track.id]) {
        setArtworkLoading(prev => ({ ...prev, [track.id]: true }));
        (async () => {
          try {
            const access = await resolveTrackMediaUrl(
              track.id,
              track.artwork!.fileId,
              'artwork',
              track.storageProvider,
            );
            setArtworkBlobs(prev => ({ ...prev, [track.id]: access.url }));
          } catch {
            setArtworkBlobs(prev => ({ ...prev, [track.id]: 'placeholder' }));
          } finally {
            setArtworkLoading(prev => ({ ...prev, [track.id]: false }));
          }
        })();
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tracks, sharedTracks]);

  // Separate cleanup effect that only runs on unmount
  useEffect(() => {
    return () => {
      Object.values(artworkBlobs).forEach(url => {
        if (url && url !== 'placeholder' && url.startsWith('blob:')) {
          URL.revokeObjectURL(url);
        }
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // formatDate and getStatusColor no longer used in this file; logic moved into TrackTile and kanbanService

  // Determine editable tracks for free users - use the same filtered tracks but sort by oldest first
  let editableTrackIds: string[] = [];
  if (userType === 'starter') {
    // Use the same filtering logic as filteredTracks but sort by oldest first to get the 3 oldest
    let tracksForEditable = tracks; // Start with all owned tracks
    
    if (!showReleased) {
      tracksForEditable = tracksForEditable.filter(t => t.status !== 'Live');
    }
    if (search.trim()) {
      const sanitizedSearch = sanitizeSearchQuery(search);
      tracksForEditable = tracksForEditable.filter(t => t.name.toLowerCase().includes(sanitizedSearch.toLowerCase()));
    }
    
    // Sort by oldest first to get the 3 oldest tracks that should be editable
    const sorted = [...tracksForEditable].sort((a, b) => new Date(a.createdDate).getTime() - new Date(b.createdDate).getTime());
    editableTrackIds = sorted.slice(0, 3).map(t => t.id);
  }

  const handleAddTrack = () => {
    if (!canCreate) return;

    const limits = getTierLimits(userType);
    const hasReachedLimit = limits.maxTracks !== -1 && tracks.length >= limits.maxTracks;
    
    if (hasReachedLimit) {
      logInfo('User attempted to add track but reached limit', { 
        currentTracks: tracks.length, 
        limit: limits.maxTracks,
        userTier: userType
      });
      if (isAnonymous) {
        setShowRequireAccount(true);
      } else {
        setShowUpgradeModal(true);
      }
    } else {
      logInfo('User navigating to create new track', { userType });
      navigate('/tracks/new');
    }
  };

  // Remove all audioBlobUrls, isPlaying, and related playback state/logic from Dashboard

  if (tracksLoading) {
    return (
      <div className="text-center py-10 text-[var(--color-muted)]">
        <CassetteLoader size={64} variant="overlay" className="justify-center mx-auto" />
      </div>
    );
  }

  return (
    <div className="w-full">
      {/* Dashboard Header - Full width with title and action button */}
      <div className="mb-8">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
          <div className="flex-1 min-w-0">
            <SketchPageTitle data-testid="dashboard-title">
              {t('dashboard.myTracks')}
            </SketchPageTitle>
            <p className="mt-2 text-sm text-[var(--color-muted)]">{t('dashboard.manageTracks')}</p>
          </div>
          <div className="mt-4 sm:mt-0 sm:flex-shrink-0">
            {/* Hide New Track button if user has reached their tier limit */}
            {(() => {
              const limits = getTierLimits(userType);
              const hasReachedLimit = limits.maxTracks !== -1 && tracks.length >= limits.maxTracks;
              return !hasReachedLimit;
            })() && (
              <SketchButton
                onClick={handleAddTrack}
                variant="primary"
                className="px-5 py-2 inline-flex items-center"
                data-testid="new-track-button"
                disabled={!canCreate}
              >
                <PlusIcon className="-ml-1 mr-2 h-5 w-5" />
                {t('dashboard.newTrack')}
              </SketchButton>
            )}
            
            {/* Show upgrade prompt for users at track limit */}
            {(() => {
              const limits = getTierLimits(userType);
              const hasReachedLimit = limits.maxTracks !== -1 && tracks.length >= limits.maxTracks;
              return hasReachedLimit;
            })() && (
              <Link
                to="/upgrade"
                className="btn-brand flex items-center gap-2 px-4 py-2 text-sm font-semibold text-white rounded-lg transition-all duration-200 transform hover:scale-105 shadow-lg hover:shadow-xl"
              >
                <SparklesIcon className="w-4 h-4" />
                {t('dashboard.upgradeToManageMore')}
              </Link>
            )}
          </div>
        </div>
      </div>
      
      {/* View Scope Tabs */}
      <div className="mb-6 border-b border-[var(--color-border)]">
        <nav className="-mb-px flex gap-2 sm:gap-4 overflow-x-auto" data-testid="dashboard-tabs">
          <button
            onClick={() => handleViewScopeChange('owned')}
            className={`py-3 px-2 sm:px-4 text-sm font-medium border-b-2 transition-colors flex items-center gap-1 sm:gap-2 whitespace-nowrap ${
              viewScope === 'owned'
                ? 'border-[var(--brand-from)] text-[var(--brand-from)]'
                : 'border-transparent text-[var(--color-muted)] hover:text-[var(--color-text)] hover:border-[var(--color-border)]'
            }`}
            data-testid="dashboard-tab-owned"
          >
            <span className="hidden sm:inline">{t('dashboard.tabs.myTracks')}</span>
            <span className="sm:hidden">{t('dashboard.tabs.myTracksShort')}</span>
            <span className="px-1.5 sm:px-2 py-0.5 text-xs rounded-full bg-gray-100 text-gray-600">
              {tracks.length}
            </span>
          </button>
          <button
            onClick={() => handleViewScopeChange('shared')}
            disabled={sharedTracks.length === 0}
            className={`py-3 px-2 sm:px-4 text-sm font-medium border-b-2 transition-colors flex items-center gap-1 sm:gap-2 whitespace-nowrap ${
              sharedTracks.length === 0
                ? 'border-transparent text-gray-400 cursor-not-allowed'
                : viewScope === 'shared'
                ? 'border-[var(--brand-from)] text-[var(--brand-from)]'
                : 'border-transparent text-[var(--color-muted)] hover:text-[var(--color-text)] hover:border-[var(--color-border)]'
            }`}
            data-testid="dashboard-tab-shared"
          >
            <UsersIcon className="h-4 w-4 flex-shrink-0" />
            <span className="hidden sm:inline">{t('dashboard.tabs.sharedWithMe')}</span>
            <span className="sm:hidden">{t('dashboard.tabs.sharedWithMeShort')}</span>
            <span className={`px-1.5 sm:px-2 py-0.5 text-xs rounded-full ${
              sharedTracks.length === 0 
                ? 'bg-gray-100 text-gray-400' 
                : 'bg-[var(--collab-blue-bg)] text-[var(--collab-blue-text)]'
            }`}>
              {sharedTracks.length}
            </span>
          </button>
          <button
            onClick={() => handleViewScopeChange('all')}
            className={`py-3 px-2 sm:px-4 text-sm font-medium border-b-2 transition-colors flex items-center gap-1 sm:gap-2 whitespace-nowrap ${
              viewScope === 'all'
                ? 'border-[var(--brand-from)] text-[var(--brand-from)]'
                : 'border-transparent text-[var(--color-muted)] hover:text-[var(--color-text)] hover:border-[var(--color-border)]'
            }`}
            data-testid="dashboard-tab-all"
          >
            <span className="hidden sm:inline">{t('dashboard.tabs.allTracks')}</span>
            <span className="sm:hidden">{t('dashboard.tabs.allTracksShort')}</span>
            <span className="px-1.5 sm:px-2 py-0.5 text-xs rounded-full bg-gray-100 text-gray-600">
              {tracks.length + sharedTracks.length}
            </span>
          </button>
        </nav>
      </div>

      {/* Filter & Sort Controls */}
      {showFilters ? (
        <div className="bg-[var(--color-surface)] rounded-xl shadow-sm border border-[var(--color-border)] mb-6">
          <div className="p-4">
            <div className="flex flex-col lg:flex-row lg:items-center gap-4">
              {/* Left side: Filters */}
              <div className="flex flex-col lg:flex-row lg:items-center gap-4 flex-1">
                {/* Sort Control */}
                <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                  <label className="text-sm font-semibold text-[var(--color-text)]/85 whitespace-nowrap">
                    {t('dashboard.sortByLabel')}
                  </label>
                  <Listbox
                    value={sortBy}
                    onChange={(newSortBy) => {
                      logInfo('User changed sort order', { from: sortBy, to: newSortBy });
                      setSortBy(newSortBy);
                      localStorage.setItem('dashboard-sortBy', newSortBy);
                    }}
                  >
                    <div className="relative">
                      <Listbox.Button className="relative w-full sm:w-auto sm:min-w-[140px] px-3 py-2 text-left bg-[var(--color-surface)] border border-[var(--color-border)] rounded-lg cursor-pointer focus:outline-none focus:ring-2 focus:ring-[var(--btn-primary-ring)] focus:border-[var(--btn-primary-ring)] hover:opacity-90 transition-colors">
                        <span className="block text-sm whitespace-nowrap">{t(`dashboard.sortBy.${sortBy}`)}</span>
                        <span className="absolute inset-y-0 right-0 flex items-center pr-2">
                          <ChevronDownIcon className="h-4 w-4" style={{ color: 'var(--color-muted)' }} aria-hidden="true" />
                        </span>
                      </Listbox.Button>
                      <Listbox.Options className="absolute z-10 w-auto min-w-full mt-1 bg-[var(--color-surface)] border border-[var(--color-border)] rounded-lg shadow-lg max-h-60 overflow-auto focus:outline-none">
                        {[
                          { value: 'created', label: t('dashboard.sortBy.created') },
                          { value: 'updated', label: t('dashboard.sortBy.updated') },
                          { value: 'name', label: t('dashboard.sortBy.name') }
                        ].map((option) => (
                          <Listbox.Option
                            key={option.value}
                            className={({ active }) =>
                              `relative cursor-pointer select-none py-2 pl-10 pr-4 text-sm ${
                                active ? 'bg-[var(--collab-blue-bg)] text-[var(--collab-blue-text)]' : 'text-[var(--color-text)]'
                              }`
                            }
                            value={option.value}
                          >
                            {({ selected }) => (
                              <>
                                <span className={`block ${selected ? 'font-medium' : 'font-normal'}`}>
                                  {option.label}
                                </span>
                                {selected ? (
                                  <span className="absolute inset-y-0 left-0 flex items-center pl-3" style={{ color: 'var(--collab-blue-text)' }}>
                                    <CheckIcon className="h-4 w-4" aria-hidden="true" />
                                  </span>
                                ) : null}
                              </>
                            )}
                          </Listbox.Option>
                        ))}
                      </Listbox.Options>
                    </div>
                  </Listbox>
                </div>

                {/* View Mode Toggle */}
                <div className="flex items-center gap-3">
                  <label htmlFor="viewMode" className="text-sm font-semibold text-[var(--color-text)]/85 whitespace-nowrap cursor-pointer">
                    {t('dashboard.kanbanView')}
                  </label>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={viewMode === 'kanban'}
                    onClick={() => {
                      const newValue = viewMode === 'tiles' ? 'kanban' : 'tiles';
                      logInfo('User toggled view mode', { from: viewMode, to: newValue });
                      setViewMode(newValue);
                      localStorage.setItem('dashboard:viewMode', newValue);
                    }}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-[var(--btn-primary-ring)] ${
                      viewMode === 'kanban' ? 'bg-[var(--brand-from)]' : 'bg-gray-300'
                    }`}
                    data-testid="view-mode-toggle"
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        viewMode === 'kanban' ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>

                {/* Show Released Toggle */}
                <div className="flex items-center gap-3">
                  <label htmlFor="showReleased" className="text-sm font-semibold text-[var(--color-text)]/85 whitespace-nowrap cursor-pointer">
                    {t('dashboard.showReleased')}
                  </label>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={showReleased}
                    onClick={() => {
                      const newValue = !showReleased;
                      logInfo('User toggled show released filter', { from: showReleased, to: newValue });
                      setShowReleased(newValue);
                    }}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-[var(--btn-primary-ring)] ${
                      showReleased ? 'bg-[var(--brand-from)]' : 'bg-gray-300'
                    }`}
                    data-testid="show-released-toggle"
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        showReleased ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>

                {/* Search Input */}
                <div className="flex-1 lg:max-w-xs">
                  <div className="relative">
                    <input
                      type="text"
                      value={search}
                      onChange={e => {
                        const newSearch = e.target.value;
                        if (newSearch !== search) {
                          logInfo('User changed search filter', { 
                            from: search, 
                            to: newSearch,
                            searchLength: newSearch.length 
                          });
                        }
                        setSearch(newSearch);
                      }}
                      placeholder={t('dashboard.filterPlaceholder')}
                      className="w-full px-4 py-2 pl-10 border rounded-lg text-sm bg-[var(--color-surface)] focus:outline-none focus:ring-2 focus:ring-[var(--btn-primary-ring)] focus:border-[var(--btn-primary-ring)] transition-colors"
                      style={{ borderColor: 'var(--color-border)', color: 'var(--color-text)' }}
                      data-testid="track-search-input"
                    />
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Search className="h-4 w-4" style={{ color: 'var(--color-muted)' }} />
                    </div>
                  </div>
                </div>
              </div>

              {/* Right side: Hide button */}
              <div className="flex justify-end">
                <button
                  onClick={() => {
                    setShowFilters(false);
                    localStorage.setItem('dashboard:showFilters', 'false');
                  }}
                  className="flex items-center gap-2 px-3 py-2 text-sm rounded-lg transition-colors"
                  style={{ color: 'var(--color-muted)' }}
                  title={t('dashboard.hideFilters')}
                >
                  <ChevronUpIcon className="w-4 h-4" />
                  {t('dashboard.hideFilters')}
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : (
        /* Show Filters Button when collapsed */
        <div className="mb-6">
          <button
            onClick={() => {
              setShowFilters(true);
              localStorage.setItem('dashboard:showFilters', 'true');
            }}
            className="flex items-center gap-2 px-4 py-2 text-sm rounded-lg transition-colors border bg-[var(--color-surface)]"
            style={{ color: 'var(--color-muted)', borderColor: 'var(--color-border)' }}
            title={t('dashboard.showFilters')}
          >
            <ChevronDownIcon className="w-4 h-4" />
            {t('dashboard.showFilters')}
          </button>
        </div>
      )}
      {playingTrackId && (
        <div className="mb-4 p-4 rounded-lg border" style={{ backgroundColor: 'var(--color-surface)', borderColor: 'var(--color-border)' }}>
          <PlaybackControls
            isPaused={isPaused}
            onPlay={() => setIsPaused(false)}
            onPause={() => setIsPaused(true)}
            onStop={() => {
              setPlayingTrackId(null);
              setIsPaused(false);
            }}
            size="md"
            className="mb-3"
          />
          <AudioWavePlayer
            trackId={playingTrackId}
            fileId={(() => {
              // Check both owned tracks and shared tracks
              const ownedTrack = tracks.find(t => t.id === playingTrackId);
              const sharedTrack = sharedTracks.find(st => st.track?.id === playingTrackId);
              const track = ownedTrack || sharedTrack?.track;
              
              if (!track?.audioFiles?.length) return '';
              const previewAudio = track.audioFiles.find((f: AudioFile) => f.isPreview) || track.audioFiles[track.audioFiles.length - 1];
              return previewAudio?.fileId || '';
            })()}
            fileName={(() => {
              // Check both owned tracks and shared tracks
              const ownedTrack = tracks.find(t => t.id === playingTrackId);
              const sharedTrack = sharedTracks.find(st => st.track?.id === playingTrackId);
              const track = ownedTrack || sharedTrack?.track;
              return track?.name || '';
            })()}
            isPaused={isPaused}
            onPause={() => setIsPaused(true)}
            onStop={() => {
              setPlayingTrackId(null);
              setIsPaused(false);
            }}
            loadingText={t('audio.loading')}
            canComment={false}
            canView={false}
            showCommentTiles={false}
            storageProvider={(() => {
              const ownedTrack = tracks.find(t => t.id === playingTrackId);
              const sharedTrack = sharedTracks.find(st => st.track?.id === playingTrackId);
              return (ownedTrack || sharedTrack?.track)?.storageProvider;
            })()}
          />
        </div>
      )}
      {viewMode === 'tiles' ? (
        <div className="w-full grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6 items-start">
        {filteredTracks.map((track) => {
          // Check if track is owned or shared
          const isOwned = tracks.some(t => t.id === track.id);
          const sharedInfo = sharedTracks.find(st => st.track?.id === track.id);
          const sharedRole = sharedInfo ? sharedInfo.share.role as 'viewer' | 'editor' | 'musician' | 'sound_engineer' | 'content_maker' : null;
          
          const previewAudio = (track.audioFiles || []).find((f: AudioFile) => f.isPreview) 
            || (track.audioFiles && track.audioFiles.length > 0 ? track.audioFiles[track.audioFiles.length - 1] : undefined);
          
          // Determine access and edit permissions
          const isAccessible = isOwned 
            ? true // Owned tracks are always accessible
            : true; // Shared tracks are always accessible (viewer or editor)
          
          // For shared tracks, check if role has any write permissions (editor, musician, sound_engineer, content_maker)
          const hasWritePermission = sharedRole === 'editor' || sharedRole === 'musician' || sharedRole === 'sound_engineer' || sharedRole === 'content_maker';
          const isEditable = isOwned 
            ? (userType !== 'starter' || editableTrackIds.includes(track.id)) // Starter users can only edit first 3 owned tracks
            : hasWritePermission; // Shared tracks: any role with write permissions (new roles work for Starter users)
          const artworkUrl = artworkBlobs[track.id] && artworkBlobs[track.id] !== 'placeholder' ? artworkBlobs[track.id] : null;
          const ownerUserId = sharedInfo ? sharedInfo.share.ownerUserId : (isOwned ? user?.uid : undefined);
          
          return (
            <div key={track.id}>
              <TrackTile
                track={track}
                isAccessible={isAccessible}
                isEditable={isEditable}
                t={t as any}
                artworkUrl={artworkUrl}
                artworkLoading={!!artworkLoading[track.id]}
                onArtworkClick={() => setArtworkModal({ trackId: track.id, url: artworkBlobs[track.id]! })}
                deleting={deletingTracks.has(track.id)}
                uncollaborating={uncollaboratingTracks.has(track.id)}
                onDelete={isOwned ? async () => {
                  logInfo('User attempted to delete track', { trackId: track.id, trackName: track.name });
                  if (window.confirm(t('dashboard.confirmRemoveTrack'))) {
                    try {
                      setDeletingTracks(prev => new Set(prev).add(track.id));
                      await deleteTrack(track.id);
                      setTracks(tracks => tracks.filter(t => t.id !== track.id));
                      logInfo('Track deleted from UI and backend', { trackId: track.id });
                    } catch (err) {
                      toast.error(t('dashboard.deleteError'));
                      logError('Failed to delete track from backend', { trackId: track.id, error: err });
                    } finally {
                      setDeletingTracks(prev => {
                        const newSet = new Set(prev);
                        newSet.delete(track.id);
                        return newSet;
                      });
                    }
                  }
                } : undefined}
                onUncollaborate={!isOwned ? async () => {
                  logInfo('User attempted to uncollaborate from track', { trackId: track.id, trackName: track.name });
                  if (window.confirm(t('dashboard.confirmUncollaborate'))) {
                    try {
                      setUncollaboratingTracks(prev => new Set(prev).add(track.id));
                      await uncollaborate(track.id);
                      // Remove from both owned tracks and shared tracks
                      setTracks(tracks => tracks.filter(t => t.id !== track.id));
                      setSharedTracks(sharedTracks => sharedTracks.filter(st => st.track.id !== track.id));
                      logInfo('User uncollaborated from track', { trackId: track.id });
                    } catch (err) {
                      toast.error(t('dashboard.uncollaborateError'));
                      logError('Failed to uncollaborate from track', { trackId: track.id, error: err });
                    } finally {
                      setUncollaboratingTracks(prev => {
                        const newSet = new Set(prev);
                        newSet.delete(track.id);
                        return newSet;
                      });
                    }
                  }
                } : undefined}
                previewAudio={previewAudio}
                isPlaying={playingTrackId === track.id && !isPaused}
                onTogglePlay={() => {
                  if (playingTrackId === track.id) {
                    // Toggle pause if same track
                    setIsPaused(!isPaused);
                  } else {
                    // Start new track
                    setPlayingTrackId(track.id);
                    setIsPaused(false);
                  }
                }}
                sharedRole={sharedRole}
                sharedCount={isOwned ? (trackShareCounts[track.id] || 0) : undefined} // Show share count for owned tracks
                isOwner={isOwned}
                ownerUserId={ownerUserId}
              />
            </div>
          );
        })}
        </div>
      ) : (
        <KanbanBoard
          tracks={filteredTracks}
          sortBy={sortBy as KanbanSort}
          isEditable={(track) => {
            const isOwned = tracks.some(t => t.id === track.id);
            const sharedInfo = sharedTracks.find(st => st.track?.id === track.id);
            const sharedRole = sharedInfo ? sharedInfo.share.role as 'viewer' | 'editor' | 'musician' | 'sound_engineer' | 'content_maker' : null;
            
            // For shared tracks, check if role has any write permissions
            const hasWritePermission = sharedRole === 'editor' || sharedRole === 'musician' || sharedRole === 'sound_engineer' || sharedRole === 'content_maker';
            return isOwned 
              ? (userType !== 'starter' || editableTrackIds.includes(track.id))
              : hasWritePermission; // New roles work for Starter users
          }}
          onMove={handleMoveKanban}
          movingIds={movingIds}
          artworkBlobs={artworkBlobs}
          artworkLoading={artworkLoading}
          onArtworkClick={(trackId, url) => setArtworkModal({ trackId, url })}
          playingTrackId={playingTrackId}
          isPaused={isPaused}
          onTogglePlay={(trackId) => {
            if (playingTrackId === trackId) {
              // Toggle pause if same track
              setIsPaused(!isPaused);
            } else {
              // Start new track
              setPlayingTrackId(trackId);
              setIsPaused(false);
            }
          }}
          onDelete={async (trackId) => {
            const track = tracks.find(t => t.id === trackId);
            if (!track) return;
            logInfo('User attempted to delete track', { trackId, trackName: track.name });
            if (window.confirm(t('dashboard.confirmRemoveTrack'))) {
              try {
                setDeletingTracks(prev => new Set(prev).add(trackId));
                await deleteTrack(trackId);
                setTracks(tracks => tracks.filter(t => t.id !== trackId));
                logInfo('Track deleted from UI and backend', { trackId });
              } catch (error) {
                logError('Failed to delete track', { trackId, error });
                toast.error(t('dashboard.deleteError'));
              } finally {
                setDeletingTracks(prev => {
                  const next = new Set(prev);
                  next.delete(trackId);
                  return next;
                });
              }
            }
          }}
          deletingTracks={deletingTracks}
          sharedRole={(track) => {
            const sharedInfo = sharedTracks.find(st => st.track?.id === track.id);
            return sharedInfo ? sharedInfo.share.role as 'viewer' | 'editor' | 'musician' | 'sound_engineer' | 'content_maker' : null;
          }}
          sharedCount={(track) => {
            const isOwned = tracks.some(t => t.id === track.id);
            return isOwned ? (trackShareCounts[track.id] || 0) : undefined;
          }}
          isOwner={(track) => tracks.some(t => t.id === track.id)}
          ownerUserId={(track) => {
            const sharedInfo = sharedTracks.find(st => st.track?.id === track.id);
            const isOwned = tracks.some(t => t.id === track.id);
            return sharedInfo ? sharedInfo.share.ownerUserId : (isOwned ? user?.uid : undefined);
          }}
        />
      )}

      {((viewScope === 'owned' && tracks.length === 0) || 
        (viewScope === 'shared' && sharedTracks.length === 0) || 
        (viewScope === 'all' && tracks.length === 0 && sharedTracks.length === 0)) && (
        <div className="text-center py-16">
          <div className="mx-auto w-24 h-24 rounded-full flex items-center justify-center mb-4" style={{ backgroundColor: 'var(--color-hover)' }}>
            <MusicalNoteIcon className="h-12 w-12" style={{ color: 'var(--color-muted)' }} />
          </div>
          <h3 className="text-lg font-medium text-[var(--color-text)] mb-2">
            {viewScope === 'shared' ? t('dashboard.noSharedTracksYet') : t('dashboard.noTracksYet')}
          </h3>
          <p className="mb-6" style={{ color: 'var(--color-muted)' }}>
            {viewScope === 'shared' 
              ? t('dashboard.sharedTracksWillAppearHere') 
              : t('dashboard.createFirstTrack')
            }
          </p>
          {viewScope !== 'shared' && (() => {
            const limits = getTierLimits(userType);
            const hasReachedLimit = limits.maxTracks !== -1 && tracks.length >= limits.maxTracks;
            return !hasReachedLimit;
          })() && (
            <Link
              to="/tracks/new"
              className="btn-primary transform hover:scale-105"
            >
              <PlusIcon className="-ml-1 mr-2 h-5 w-5" />
              {t('dashboard.newTrack')}
            </Link>
          )}
        </div>
      )}

      {/* Artwork Modal */}
      {artworkModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-70" onClick={() => setArtworkModal(null)}>
          <img
            src={artworkModal.url}
            alt={t('dashboard.artworkLargePreview')}
            className="max-w-full max-h-[80vh] rounded-lg shadow-2xl border-4 border-white"
            onClick={e => e.stopPropagation()}
          />
        </div>
      )}

      {/* Upgrade modal for free users at track limit */}
      {showUpgradeModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
          <div className="rounded-xl shadow-lg p-8 max-w-sm w-full flex flex-col items-center border" style={{ backgroundColor: 'var(--color-surface)', borderColor: 'var(--color-border)' }}>
            <h2 className="text-xl font-bold mb-2 text-lime-700">{t('upgrade.title')}</h2>
            <p className="mb-4 text-center" style={{ color: 'var(--color-text)' }}>{t('upgrade.description')}</p>
            <button
              className="w-full px-4 py-2 rounded btn-primary-reverse font-semibold text-lg transition mb-2"
              onClick={() => {
                logInfo('User clicked upgrade button from dashboard modal');
                navigate('/upgrade');
              }}
            >
              {t('upgrade.seePlans')}
            </button>
            <button
              className="w-full px-4 py-2 rounded font-semibold transition"
              style={{ backgroundColor: 'var(--color-hover)', color: 'var(--color-text)' }}
              onClick={() => {
                logInfo('User dismissed upgrade modal');
                setShowUpgradeModal(false);
              }}
            >
              {t('buttons.cancel')}
            </button>
          </div>
        </div>
      )}

      <RequireAccountModal
        isOpen={showRequireAccount}
        onClose={() => setShowRequireAccount(false)}
        onLinkGoogle={() => { setShowRequireAccount(false); void linkWithGoogle(); }}
        onLinkApple={() => { setShowRequireAccount(false); void linkWithApple(); }}
      />

      {/* Status change notification */}
      {statusChangeNotification && (
        <StatusChangeNotification
          trackName={statusChangeNotification.trackName}
          newStatus={statusChangeNotification.newStatus}
          success={statusChangeNotification.success}
          onClose={() => setStatusChangeNotification(null)}
        />
      )}

    </div>
  );
};

export default Dashboard; 